/**
 * Типы ответов HTTP API. Перенесены один в один из `backend/app/schemas`;
 * значения `datetime` приходят строками ISO-8601.
 */

export type Kind =
  | "Catalog"
  | "Document"
  | "DocumentJournal"
  | "Enum"
  | "Constant"
  | "ChartOfCharacteristicTypes"
  | "ChartOfAccounts"
  | "ChartOfCalculationTypes"
  | "InformationRegister"
  | "AccumulationRegister"
  | "AccountingRegister"
  | "CalculationRegister"
  | "BusinessProcess"
  | "Task"
  | "ExchangePlan"
  | "Sequence"
  | "FilterCriterion"
  | "DataProcessor"
  | "Report"
  | "Unknown";

export type PartKind = "none" | "tabular" | "virtual" | "records";

export type TableSort = "name" | "object" | "kind" | "fields";

export type SortOrder = "asc" | "desc";

export type SyncMode = "full_refresh" | "upsert" | "append";

export type TypePolicy = "native" | "all_text";

export type DeleteDetection = "none" | "key_scan";

export type RunStatus = "running" | "success" | "failed" | "cancelled";

export type RunTrigger = "manual" | "schedule";

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export interface Message {
  detail: string;
}

export interface ErrorBody {
  detail: string;
  code: string;
  hint: string | null;
}

export interface TableSummary {
  entity_set: string;
  kind: Kind;
  kind_title: string;
  object_name: string;
  part: string;
  part_title: string;
  part_kind: PartKind;
  parent_set: string;
  field_count: number;
  child_count: number;
  subordinate: boolean;
  synced: boolean;
}

export interface KindFacet {
  kind: Kind;
  title: string;
  count: number;
}

export interface TablesResponse {
  items: TableSummary[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
  kinds: KindFacet[];
}

export interface ColumnRef {
  composite: boolean;
  nav_prop: string;
  type_field: string;
  targets: string[];
}

export interface Column {
  name: string;
  edm_type: string;
  type_1c: string;
  pg_type: string;
  nullable: boolean;
  key: boolean;
  collection: boolean;
  selectable: boolean;
  storage: boolean;
  title: string;
  note: string;
  ref: ColumnRef | null;
}

export interface ChildTable {
  entity_set: string;
  part: string;
  part_title: string;
  part_kind: PartKind;
  field_count: number;
}

export interface RelationOut {
  name: string;
  target: string;
  collection: boolean;
  kind: Kind;
}

export type DataRow = Record<string, unknown>;

export interface PreviewData {
  columns: string[];
  rows: DataRow[];
  total: number | null;
  truncated: boolean;
  elapsed_ms: number;
  error: string | null;
}

export interface JobBrief {
  id: string;
  name: string;
  enabled: boolean;
  cron: string | null;
  last_status: string | null;
  last_run_at: string | null;
}

export interface TableDetail {
  table: TableSummary;
  columns: Column[];
  key_columns: string[];
  children: ChildTable[];
  relations: RelationOut[];
  row_count: number | null;
  preview: PreviewData;
  jobs: JobBrief[];
  suggested_watermark: string | null;
}

export interface MetadataInfo {
  service_url: string;
  publication: string;
  user: string;
  tables_total: number;
  tables_visible: number;
  fetched_at: string;
  age_seconds: number;
  size_bytes: number;
  edmx_version: string;
  namespaces: string[];
}

export interface TargetIn {
  name: string;
  dsn: string;
  schema_name: string;
}

export interface TargetOut {
  id: string;
  name: string;
  schema_name: string;
  host: string;
  database: string;
  user: string;
  created_at: string;
  last_checked_at: string | null;
  last_error: string | null;
}

export interface TargetCheck {
  ok: boolean;
  server_version: string | null;
  schema_exists: boolean | null;
  can_create: boolean | null;
  error: string | null;
  elapsed_ms: number;
}

export interface TableSize {
  name: string;
  total_bytes: number;
  table_bytes: number;
  index_bytes: number;
  rows: number | null;
  rows_exact: boolean;
}

export interface TargetSize {
  target_id: string;
  target_name: string;
  database: string;
  database_bytes: number;
  schema_name: string;
  schema_exists: boolean;
  schema_bytes: number;
  tables: TableSize[];
  rows_total: number | null;
  rows_exact: boolean;
  elapsed_ms: number;
}

export interface JobIn {
  name: string;
  entity_set: string;
  target_id: string;
  target_schema?: string | null;
  target_table?: string | null;
  mode?: SyncMode;
  columns?: string[] | null;
  odata_filter?: string | null;
  key_columns?: string[] | null;
  watermark_column?: string | null;
  detect_deletes?: DeleteDetection;
  page_size?: number;
  type_policy?: TypePolicy;
  cron?: string | null;
  enabled?: boolean;
}

export interface JobPatch {
  name?: string | null;
  target_schema?: string | null;
  target_table?: string | null;
  mode?: SyncMode | null;
  columns?: string[] | null;
  odata_filter?: string | null;
  key_columns?: string[] | null;
  watermark_column?: string | null;
  detect_deletes?: DeleteDetection | null;
  page_size?: number | null;
  type_policy?: TypePolicy | null;
  cron?: string | null;
  enabled?: boolean | null;
}

export interface RunStats {
  rows_read: number;
  rows_inserted: number;
  rows_updated: number;
  rows_unchanged: number;
  rows_deleted: number;
  batches: number;
  source_total: number | null;
}

export interface JobOut {
  id: string;
  name: string;
  source_id: string | null;
  source_name: string;
  entity_set: string;
  kind_title: string;
  target_id: string;
  target_name: string;
  target_schema: string;
  target_table: string;
  qualified_table: string;
  mode: SyncMode;
  columns: string[] | null;
  column_count: number | null;
  odata_filter: string | null;
  key_columns: string[];
  watermark_column: string | null;
  watermark_value: string | null;
  detect_deletes: DeleteDetection;
  page_size: number;
  type_policy: TypePolicy;
  cron: string | null;
  cron_description: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  last_run_at: string | null;
  last_status: RunStatus | null;
  last_duration_ms: number | null;
  last_stats: RunStats | null;
  next_run_at: string | null;
  target_rows: number | null;
}

export interface JobPlan {
  entity_set: string;
  qualified_table: string;
  mode: SyncMode;
  key_columns: string[];
  watermark_column: string | null;
  columns: Record<string, string>[];
  ddl: string[];
  table_exists: boolean;
  missing_columns: string[];
  source_filter: string | null;
  estimated_rows: number | null;
  warnings: string[];
}

export interface RunOut {
  id: string;
  job_id: string;
  job_name: string;
  entity_set: string;
  qualified_table: string;
  trigger: RunTrigger;
  status: RunStatus;
  mode: SyncMode;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  stats: RunStats;
  error: string | null;
  watermark_before: string | null;
  watermark_after: string | null;
}

export interface RunLogLine {
  seq: number;
  ts: string;
  level: string;
  message: string;
}

export interface RunLogs {
  run_id: string;
  status: RunStatus;
  lines: RunLogLine[];
  next_seq: number;
}

export interface ExportRequest {
  entity_set: string;
  target_id: string;
  target_schema?: string | null;
  target_table?: string | null;
  mode?: SyncMode;
  columns?: string[] | null;
  odata_filter?: string | null;
  watermark_column?: string | null;
  detect_deletes?: DeleteDetection;
  page_size?: number;
  type_policy?: TypePolicy;
  cron?: string | null;
  save_job?: boolean;
}

export interface ExportAccepted {
  run_id: string;
  job_id: string;
  status: RunStatus;
}

export type DumpStatus =
  "running" | "success" | "partial" | "failed" | "cancelled";

export type DumpItemStatus =
  "pending" | "running" | "success" | "failed" | "skipped" | "cancelled";

export interface DumpRequest {
  target_id: string;
  target_schema?: string | null;
  mode?: SyncMode;
  kinds?: string[];
  entity_sets?: string[];
  include_subordinate?: boolean;
  only_tabular?: boolean;
  page_size?: number;
  type_policy?: TypePolicy;
  parallel?: number | null;
}

export interface DumpTotals {
  tables: number;
  done: number;
  failed: number;
  skipped: number;
  running: number;
  pending: number;
  rows_read: number;
  rows_inserted: number;
  rows_updated: number;
  rows_unchanged: number;
  rows_total: number;
  tables_counted: number;
}

export interface DumpItemProgress {
  entity_set: string;
  object_name: string;
  rows_read: number;
  source_total: number | null;
  progress: number | null;
}

export type DumpItemSort =
  | "order"
  | "entity_set"
  | "object_name"
  | "kind_title"
  | "status"
  | "rows_read"
  | "source_total"
  | "progress"
  | "rows_inserted"
  | "duration_ms"
  | "finished_at";

export interface DumpOut {
  id: string;
  source_id: string | null;
  source_name: string;
  target_id: string;
  target_name: string;
  schema_name: string;
  mode: SyncMode;
  page_size: number;
  type_policy: TypePolicy;
  parallel: number;
  include_subordinate: boolean;
  only_tabular: boolean;
  kinds: string[];
  status: DumpStatus;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  totals: DumpTotals;
  running: DumpItemProgress[];
  restarts: number;
  resumed_at: string | null;
  error: string | null;
}

export interface DumpItemOut {
  entity_set: string;
  object_name: string;
  kind_title: string;
  target_table: string;
  status: DumpItemStatus;
  rows_read: number;
  rows_inserted: number;
  rows_updated: number;
  rows_unchanged: number;
  source_total: number | null;
  progress: number | null;
  duration_ms: number | null;
  error: string | null;
  error_kind: string | null;
  error_title: string;
  options: DumpRetryOptions | null;
  notes: string[];
  view_of: string | null;
  sliced: boolean;
  started_at: string | null;
  finished_at: string | null;
}

export type RetryStart = "resume" | "reload" | "recreate";

export interface DumpRetryOptions {
  start: RetryStart;
  page_size?: number | null;
  type_policy?: TypePolicy | null;
  include_storage?: boolean;
}

export interface DumpRetryRequest {
  entity_sets?: string[];
  error_kinds?: string[];
  include_cancelled?: boolean;
  options?: DumpRetryOptions;
}

export interface DumpErrorGroup {
  kind: string;
  title: string;
  hint: string;
  count: number;
}

export interface DumpAccepted {
  dump_id: string;
  status: DumpStatus;
  tables: number;
}

export interface Overview {
  tables_total: number;
  tables_visible: number;
  jobs_total: number;
  jobs_enabled: number;
  jobs_scheduled: number;
  runs_today: number;
  runs_failed_today: number;
  rows_synced_today: number;
  targets: number;
  running: RunOut[];
  recent: RunOut[];
  next_runs: JobOut[];
  metadata_age_seconds: number | null;
  scheduler_enabled: boolean;
}

export type Role = "admin";

export interface LoginRequest {
  login: string;
  password: string;
}

export interface UserInfo {
  login: string;
  role: Role;
  expires_at: string;
}

export interface LoginResponse {
  token: string;
  expires_at: string;
  user: UserInfo;
}

export interface SourceCheck {
  ok: boolean;
  at: string;
  error: string | null;
  entity_sets: number | null;
  publication: string | null;
}

export interface SourceRejection {
  reason: string;
  at: string;
}

export interface SourceOut {
  id: string;
  name: string;
  url: string;
  publication: string;
  login: string;
  verify_tls: boolean;
  has_password: boolean;
  rejected: SourceRejection | null;
  check: SourceCheck | null;
  jobs: number;
  created_at: string;
  updated_at: string;
}

export interface SourceIn {
  name: string;
  url: string;
  login: string;
  password: string;
  verify_tls: boolean;
}

export interface SourcePatch {
  name?: string;
  url?: string;
  login?: string;
  password?: string;
  verify_tls?: boolean;
}

export interface SourcePassword {
  password: string;
}

export interface CountResponse {
  count: number | null;
}

export interface TableCounts {
  counts: Record<string, number | null>;
  elapsed_ms: number;
}

export interface HealthResponse {
  status: string;
  mongo: boolean;
  version: string;
}

export const KIND_TITLES: Readonly<Record<Kind, string>> = {
  Catalog: "Справочник",
  Document: "Документ",
  DocumentJournal: "Журнал документов",
  Enum: "Перечисление",
  Constant: "Константа",
  ChartOfCharacteristicTypes: "План видов характеристик",
  ChartOfAccounts: "План счетов",
  ChartOfCalculationTypes: "План видов расчёта",
  InformationRegister: "Регистр сведений",
  AccumulationRegister: "Регистр накопления",
  AccountingRegister: "Регистр бухгалтерии",
  CalculationRegister: "Регистр расчёта",
  BusinessProcess: "Бизнес-процесс",
  Task: "Задача",
  ExchangePlan: "План обмена",
  Sequence: "Последовательность",
  FilterCriterion: "Критерий отбора",
  DataProcessor: "Обработка",
  Report: "Отчёт",
  Unknown: "Прочее",
};

export const PAGE_SIZE_MAX = 50_000;
export const DUMP_PAGE_SIZE = 50_000;
export const DUMP_PARALLEL = 10;
export const DUMP_PARALLEL_MAX = 16;

export const RUN_STATUS_TITLES: Readonly<Record<RunStatus, string>> = {
  running: "Выполняется",
  success: "Успешно",
  failed: "Ошибка",
  cancelled: "Отменён",
};

export const SYNC_MODE_TITLES: Readonly<Record<SyncMode, string>> = {
  full_refresh: "Полная перезагрузка",
  upsert: "Обновление по ключу",
  append: "Только добавление",
};

export const TYPE_POLICY_TITLES: Readonly<Record<TypePolicy, string>> = {
  native: "По типам 1С",
  all_text: "Всё в text",
};

export const TYPE_POLICY_HINTS: Readonly<Record<TypePolicy, string>> = {
  native:
    "Колонки получают типы uuid, numeric, timestamp, boolean, bigint; незнакомые типы — text. Пустые даты и ссылки 1С становятся NULL, кроме ключей.",
  all_text: "Все колонки — text, значения лежат ровно так, как их отдала 1С.",
};

export const DELETE_DETECTION_TITLES: Readonly<
  Record<DeleteDetection, string>
> = {
  none: "Не отслеживать",
  key_scan: "Сверка ключей",
};

export const RUN_TRIGGER_TITLES: Readonly<Record<RunTrigger, string>> = {
  manual: "Вручную",
  schedule: "По расписанию",
};

export const DUMP_STATUS_TITLES: Readonly<Record<DumpStatus, string>> = {
  running: "Выполняется",
  success: "Готово",
  partial: "Готово с ошибками",
  failed: "Ошибка",
  cancelled: "Прерван",
};

export const DUMP_ITEM_STATUS_TITLES: Readonly<Record<DumpItemStatus, string>> =
  {
    pending: "В очереди",
    running: "Выгружается",
    success: "Готово",
    failed: "Ошибка",
    skipped: "Пропущена",
    cancelled: "Не выгружена",
  };
